import type {
  AccessSnapshot, AdminCharacterBinding, AdminCharacterCreator, AdminCharacterDeleteResult, AdminCharacterImage, AdminCharacterProfile, AdminCharacterSdPrompts, AdminCharacterVoice, AdminContextPolicySnapshot, AdminRole, AdminUsageSnapshot, AvatarSelection, InteractionSelection,
  CharacterImageKind, CharacterProfile, GuildMemberPage, InteractionBlockDetail, InteractionBlockPage,
  ImagePromptPreset, ImagePromptPresetPage, ImageUsageSnapshot, ManualImageMetadataSource, ManualImagePublicJob, MemoryRecord, SettingsContext, SettingsSnapshot, UnlockedPersona, UsageWindow,
  CheckedVoiceSource, SynthesisSettings, UserCharacterImagePrompts, UserCharacterSummary, UserCharacterVoice, VoiceSource, VoiceSourcePage, VoiceStudioJob,
} from "./types";
import { isDiscordContextMaxMessages } from "../../shared/discord-context-policy";
import { discordImageJobSourceFromRequestId } from "../../shared/discord-image-source";
import { parseDiscordVoiceSynthesisSettings } from "../../shared/discord-voice-settings";

const CHARACTER_ID_PATTERN = /^[a-z0-9][a-z0-9_-]{0,63}$/;
const DISCORD_UID_PATTERN = /^\d{16,20}$/;
const OPAQUE_ID_PATTERN = /^[A-Za-z0-9._~:+\/-]{1,200}$/;
const CURSOR_PATTERN = /^[A-Za-z0-9._~|:+=\/-]{1,512}$/;
const CHARACTER_IMAGE_VERSION_PATTERN = /^[A-Za-z0-9._~-]{1,200}$/;

function synthesisSettings(value: unknown, code: string): SynthesisSettings {
  const settings = parseDiscordVoiceSynthesisSettings(value);
  if (settings === null) throw new Error(code);
  return settings;
}

export function parseCheckedVoiceSource(value: unknown): CheckedVoiceSource {
  const source = objectValue(value, "voice_source_check_response_invalid");
  if (typeof source.id !== "string" || !/^[0-9a-f]{32}$/.test(source.id)
    || typeof source.name !== "string" || source.name.trim().length === 0 || source.name.length > 200) {
    throw new Error("voice_source_check_response_invalid");
  }
  return { id: source.id, name: source.name };
}

function voiceStudioJob(value: unknown): VoiceStudioJob {
  const job = objectValue(value, "voice_studio_job_response_invalid");
  const status = job.status;
  const audioUrl = job.audio_url;
  if (
    typeof job.id !== "string" || !OPAQUE_ID_PATTERN.test(job.id)
    || typeof job.title !== "string" || job.title.trim().length === 0 || job.title.length > 200
    || !(status === "queued" || status === "generating" || status === "completed" || status === "failed")
    || typeof job.text !== "string" || job.text.trim().length === 0 || job.text.length > 20_000
    || typeof job.voice_id !== "string" || !/^[0-9a-f]{32}$/.test(job.voice_id)
    || !(job.duration_ms === null || (typeof job.duration_ms === "number" && Number.isSafeInteger(job.duration_ms) && job.duration_ms >= 0))
    || !(job.error_code === null || (typeof job.error_code === "string" && /^[a-z0-9_-]{1,100}$/.test(job.error_code)))
    || !(audioUrl === null || (typeof audioUrl === "string" && new RegExp(`^/v1/discord-settings/voice-studio/jobs/${job.id.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}/audio$`).test(audioUrl)))
  ) throw new Error("voice_studio_job_response_invalid");
  if ((status === "completed") !== (typeof audioUrl === "string")) throw new Error("voice_studio_job_response_invalid");
  return {
    id: job.id,
    title: job.title,
    status,
    text: job.text,
    voice_id: job.voice_id,
    synthesis_settings: synthesisSettings(job.synthesis_settings, "voice_studio_job_response_invalid"),
    duration_ms: job.duration_ms as number | null,
    error_code: job.error_code as string | null,
    created_at: timestamp(job.created_at, "voice_studio_job_response_invalid"),
    audio_url: audioUrl as string | null,
  };
}

export function parseVoiceStudioJobs(value: unknown): VoiceStudioJob[] {
  const payload = objectValue(value, "voice_studio_jobs_response_invalid");
  if (!Array.isArray(payload.jobs)) throw new Error("voice_studio_jobs_response_invalid");
  return payload.jobs.map(voiceStudioJob);
}

export function parseVoiceStudioJob(value: unknown): VoiceStudioJob {
  return voiceStudioJob(value);
}

function bindingVersion(value: unknown, code: string): number {
  if (typeof value !== "number" || !Number.isInteger(value) || value < 0 || !Number.isSafeInteger(value)) throw new Error(code);
  return value;
}

function parseAvatarSelection(value: unknown): AvatarSelection | null {
  if (value === undefined || value === null) return null;
  const avatar = objectValue(value, "settings_response_invalid");
  if (typeof avatar.consented !== "boolean") throw new Error("settings_response_invalid");
  return { binding_version: bindingVersion(avatar.binding_version, "settings_response_invalid"), consented: avatar.consented };
}

function parseInteractionSelection(value: unknown): InteractionSelection | null {
  if (value === undefined || value === null) return null;
  const interaction = objectValue(value, "settings_response_invalid");
  if ((interaction.authority_kind !== "creator" && interaction.authority_kind !== "avatar") || typeof interaction.consented !== "boolean") {
    throw new Error("settings_response_invalid");
  }
  return {
    authority_kind: interaction.authority_kind,
    authority_revision: bindingVersion(interaction.authority_revision, "settings_response_invalid"),
    consented: interaction.consented,
  };
}

function parseSettingsContext(value: unknown): SettingsContext {
  if (value === undefined || value === null) return { avatar: null };
  const context = objectValue(value, "settings_response_invalid");
  let avatar: SettingsContext["avatar"] = null;
  if (context.avatar !== undefined && context.avatar !== null) {
    const value = objectValue(context.avatar, "settings_response_invalid");
    if (typeof value.character_id !== "string" || !CHARACTER_ID_PATTERN.test(value.character_id) || typeof value.consented !== "boolean") throw new Error("settings_response_invalid");
    avatar = { character_id: value.character_id, binding_version: bindingVersion(value.binding_version, "settings_response_invalid"), consented: value.consented };
  }
  let interaction: SettingsContext["interaction"] = null;
  if (context.interaction !== undefined && context.interaction !== null) {
    const value = objectValue(context.interaction, "settings_response_invalid");
    if (typeof value.character_id !== "string" || !CHARACTER_ID_PATTERN.test(value.character_id)) throw new Error("settings_response_invalid");
    const state = parseInteractionSelection(value);
    if (!state) throw new Error("settings_response_invalid");
    interaction = { character_id: value.character_id, ...state };
  }
  return { avatar, interaction };
}

function isMemoryRecord(value: unknown): value is MemoryRecord {
  if (!value || typeof value !== "object") return false;
  const memory = value as Record<string, unknown>;
  return typeof memory.id === "number"
    && typeof memory.created_date === "string"
    && typeof memory.permanent === "boolean"
    && typeof memory.memory === "string";
}

function parseCharacterProfile(value: unknown, expectedId: string): CharacterProfile | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  const profile = value as Record<string, unknown>;
  const textFields = [
    "name", "affiliation", "appearance", "outfit", "speech_style",
    "movement_style", "personality", "background", "description", "sd_positive_prompt",
  ] as const;
  const profileTextAbsent = profile.profile_text === undefined && profile.profile_revision === undefined;
  const profileTextValid = typeof profile.profile_text === "string"
    && profile.profile_text.trim().length > 0
    && profile.profile_text.length <= 20_000
    && typeof profile.profile_revision === "number"
    && Number.isInteger(profile.profile_revision)
    && profile.profile_revision >= 0;
  if (
    profile.id !== expectedId
    || !textFields.every((field) => typeof profile[field] === "string" && (profile[field] as string).trim().length > 0)
    || typeof profile.age !== "number"
    || !Number.isFinite(profile.age)
    || !Number.isInteger(profile.age)
    || profile.age < 18
    || typeof profile.height_cm !== "number"
    || !Number.isFinite(profile.height_cm)
    || profile.height_cm <= 0
    || !Array.isArray(profile.speech_examples)
    || profile.speech_examples.length === 0
    || !profile.speech_examples.every((example) => typeof example === "string" && example.trim().length > 0)
    || typeof profile.sd_negative_prompt !== "string"
    || !(profile.sd_revision === undefined || (typeof profile.sd_revision === "number" && Number.isSafeInteger(profile.sd_revision) && profile.sd_revision >= 0))
    || !(profile.portrait_revision === undefined || (typeof profile.portrait_revision === "number" && Number.isSafeInteger(profile.portrait_revision) && profile.portrait_revision >= 0))
    || !(profile.cover_revision === undefined || (typeof profile.cover_revision === "number" && Number.isSafeInteger(profile.cover_revision) && profile.cover_revision >= 0))
    || !isCharacterImageUrl(profile.cover_url, expectedId, "cover")
    || !isCharacterImageUrl(profile.portrait_url, expectedId, "portrait")
    || (!profileTextAbsent && !profileTextValid)
  ) return null;
  return profile as unknown as CharacterProfile;
}

export function isCharacterImageUrl(value: unknown, characterId: string, kind: CharacterImageKind): value is string {
  if (typeof value !== "string" || !CHARACTER_ID_PATTERN.test(characterId)) return false;
  if (new RegExp(`^/characters/v[2-9]/${characterId}-${kind}\\.png$`).test(value)) return true;
  const prefix = `/v1/discord-settings/character-images/${characterId}/${kind}/`;
  return value.startsWith(prefix) && CHARACTER_IMAGE_VERSION_PATTERN.test(value.slice(prefix.length));
}

function parseUnlockedPersona(value: unknown): UnlockedPersona | null {
  if (!value || typeof value !== "object") return null;
  const persona = value as Record<string, unknown>;
  if (
    typeof persona.name !== "string"
    || persona.name.trim() !== persona.name
    || !CHARACTER_ID_PATTERN.test(persona.name)
    || typeof persona.title !== "string"
    || persona.title.trim() !== persona.title
    || [...persona.title].length === 0
    || [...persona.title].length > 100
  ) {
    return null;
  }
  const description = persona.description === undefined ? "" : persona.description;
  if (
    typeof description !== "string"
    || description.trim() !== description
    || [...description].length > 20
  ) {
    return null;
  }
  const character = parseCharacterProfile(persona.character, persona.name);
  if (!character || character.name !== persona.title) return null;
  let avatar: AvatarSelection | null;
  let interaction: InteractionSelection | null;
  try {
    avatar = parseAvatarSelection(persona.avatar);
    interaction = parseInteractionSelection(persona.interaction);
  } catch {
    return null;
  }
  let statistics;
  if (persona.statistics !== undefined) {
    const stats = objectValue(persona.statistics, "settings_response_invalid");
    if (!["profile_revision", "profile_char_count", "usage_count", "dialogue_count"].every(key => typeof stats[key] === "number" && Number.isSafeInteger(stats[key]) && (stats[key] as number) >= 0)
      || stats.dialogue_count_window_days !== 7) throw new Error("settings_response_invalid");
    statistics = { profile_revision: stats.profile_revision as number, profile_char_count: stats.profile_char_count as number, usage_count: stats.usage_count as number, dialogue_count: stats.dialogue_count as number, dialogue_count_window_days: 7 as const };
  }
  return { name: persona.name, title: persona.title, description, character, avatar, interaction, ...(statistics ? { statistics } : {}) };
}

export function parseSettingsSnapshot(value: unknown): SettingsSnapshot {
  if (!value || typeof value !== "object") throw new Error("settings_response_invalid");
  const snapshot = value as Record<string, unknown>;
  const stringFields = [
    "uid",
    "system_prompt",
    "style_guide",
    "image_model",
    "image_cfg",
    "image_sampler",
    "image_scheduler",
    "image_resolution",
    "title",
  ] as const;

  if (!stringFields.every((field) => typeof snapshot[field] === "string")
    || typeof snapshot.title_enabled !== "boolean"
    || typeof snapshot.revision !== "number"
    || !Number.isInteger(snapshot.revision)
    || !Array.isArray(snapshot.memories)
    || !snapshot.memories.every(isMemoryRecord)
  ) {
    throw new Error("settings_response_invalid");
  }

  const parsedUnlocked = Array.isArray(snapshot.unlocked_personas)
    ? snapshot.unlocked_personas.map(parseUnlockedPersona)
    : [];
  const unlocked = parsedUnlocked.every((persona): persona is UnlockedPersona => persona !== null)
    ? parsedUnlocked
    : [];
  const names = unlocked.map((persona) => persona.name);
  const usageCountsRecord = objectValue(snapshot.character_usage_counts, "settings_response_invalid");
  const characterUsageCounts = Object.fromEntries(Object.entries(usageCountsRecord).map(([characterId, count]) => {
    if (!CHARACTER_ID_PATTERN.test(characterId)) throw new Error("settings_response_invalid");
    return [characterId, bindingVersion(count, "settings_response_invalid")];
  }));
  if (!names.every((name) => Object.hasOwn(characterUsageCounts, name))) throw new Error("settings_response_invalid");
  const personaAvailable = typeof snapshot.selected_persona === "string"
    && new Set(names).size === names.length
    && names.includes(snapshot.selected_persona);

  return {
    ...(snapshot as unknown as Omit<SettingsSnapshot, "selected_persona" | "unlocked_personas" | "character_usage_counts" | "context" | "persona_available" | "has_locked_personas">),
    selected_persona: personaAvailable ? snapshot.selected_persona as string : "",
    unlocked_personas: personaAvailable ? unlocked : [],
    character_usage_counts: characterUsageCounts,
    context: parseSettingsContext(snapshot.context),
    persona_available: personaAvailable,
    has_locked_personas: typeof snapshot.has_locked_personas === "boolean"
      ? snapshot.has_locked_personas
      : true,
  };
}

function objectValue(value: unknown, code: string): Record<string, unknown> {
  if (!value || typeof value !== "object" || Array.isArray(value)) throw new Error(code);
  return value as Record<string, unknown>;
}

function nonNegativeNumber(value: unknown, code: string): number {
  if (typeof value !== "number" || !Number.isFinite(value) || value < 0) throw new Error(code);
  return value;
}

function usageWindow(value: unknown): UsageWindow {
  const record = objectValue(value, "admin_usage_response_invalid");
  const cell = (value: unknown) => {
    const entry = objectValue(value, "admin_usage_response_invalid");
    const amount = (candidate: unknown) => candidate === null ? null : nonNegativeNumber(candidate, "admin_usage_response_invalid");
    return { actual_usd: amount(entry.actual_usd), estimated_usd: amount(entry.estimated_usd) };
  };
  return {
    today: cell(record.today), d7: cell(record.d7), d30: cell(record.d30),
  };
}

export function parseAccessSnapshot(value: unknown): AccessSnapshot {
  const record = objectValue(value, "settings_access_response_invalid");
  const deletion = objectValue(record.title_deletion, "settings_access_response_invalid");
  if (
    typeof record.is_admin !== "boolean"
    || !(record.has_avatar === undefined || typeof record.has_avatar === "boolean")
    || !(record.has_interactions === undefined || typeof record.has_interactions === "boolean")
    || typeof deletion.can_delete !== "boolean"
    || typeof deletion.deleted_today !== "boolean"
    || typeof deletion.reset_at !== "string"
    || deletion.time_zone !== "Asia/Shanghai"
    || !(deletion.can_edit === undefined || typeof deletion.can_edit === "boolean")
    || !(deletion.title_kind === undefined || deletion.title_kind === null || deletion.title_kind === "standard" || deletion.title_kind === "special")
    || !(deletion.effective_title === undefined || deletion.effective_title === null || (typeof deletion.effective_title === "string" && deletion.effective_title.trim().length > 0 && [...deletion.effective_title].length <= 100))
  ) throw new Error("settings_access_response_invalid");
  return {
    is_admin: record.is_admin,
    has_avatar: record.has_avatar === true,
    has_interactions: record.has_interactions === true,
    title_deletion: {
      can_delete: deletion.can_delete,
      deleted_today: deletion.deleted_today,
      reset_at: deletion.reset_at,
      time_zone: "Asia/Shanghai",
      can_edit: deletion.can_edit === undefined ? true : deletion.can_edit,
      title_kind: deletion.title_kind === undefined ? null : deletion.title_kind,
      effective_title: deletion.effective_title === undefined ? null : deletion.effective_title,
    },
  };
}

function boundedText(value: unknown, maxLength: number, code: string): string {
  if (typeof value !== "string" || value.trim().length === 0 || value.length > maxLength || /[\u0000-\u0008\u000b\u000c\u000e-\u001f\u007f]/u.test(value)) throw new Error(code);
  return value;
}

function nullableCursor(value: unknown, code: string): string | null {
  if (value === undefined || value === null) return null;
  if (typeof value !== "string" || !CURSOR_PATTERN.test(value)) throw new Error(code);
  return value;
}

function timestamp(value: unknown, code: string): string {
  if (typeof value !== "string" || value.length > 64 || Number.isNaN(Date.parse(value))) throw new Error(code);
  return value;
}

export function parseAdminCharacters(value: unknown): AdminCharacterBinding[] {
  const record = objectValue(value, "admin_characters_response_invalid");
  if (!Array.isArray(record.characters)) throw new Error("admin_characters_response_invalid");
  return record.characters.map((item) => {
    const character = objectValue(item, "admin_characters_response_invalid");
    if (typeof character.id !== "string" || !CHARACTER_ID_PATTERN.test(character.id)) throw new Error("admin_characters_response_invalid");
    const ownerUid = character.owner_uid;
    const ownerName = character.owner_name;
    const unbound = ownerUid === null && ownerName === null;
    const bound = typeof ownerUid === "string" && DISCORD_UID_PATTERN.test(ownerUid)
      && (ownerName === null || (typeof ownerName === "string" && ownerName.trim().length > 0 && ownerName.length <= 100));
    if (!unbound && !bound) throw new Error("admin_characters_response_invalid");
    return {
      id: character.id,
      name: boundedText(character.name, 100, "admin_characters_response_invalid"),
      owner_uid: ownerUid as string | null,
      owner_name: ownerName as string | null,
      binding_version: bindingVersion(character.binding_version, "admin_characters_response_invalid"),
    };
  });
}

export function parseAdminCharacter(value: unknown): AdminCharacterBinding {
  return parseAdminCharacters({ characters: [value] })[0]!;
}

export function parseAdminCharacterCreator(value: unknown): AdminCharacterCreator {
  const creator = objectValue(value, "admin_character_creator_response_invalid");
  if (typeof creator.id !== "string" || !CHARACTER_ID_PATTERN.test(creator.id)) throw new Error("admin_character_creator_response_invalid");
  const creatorUid = creator.creator_uid;
  const creatorName = creator.creator_name;
  const system = creatorUid === null && creatorName === null;
  const player = typeof creatorUid === "string" && DISCORD_UID_PATTERN.test(creatorUid)
    && (creatorName === null || (typeof creatorName === "string" && creatorName.trim().length > 0 && creatorName.length <= 100));
  if (!system && !player) throw new Error("admin_character_creator_response_invalid");
  return {
    id: creator.id,
    creator_uid: creatorUid as string | null,
    creator_name: creatorName as string | null,
    creator_revision: bindingVersion(creator.creator_revision, "admin_character_creator_response_invalid"),
  };
}

export function parseUserCharacters(value: unknown): UserCharacterSummary[] {
  const response = objectValue(value, "user_characters_response_invalid");
  if (!Array.isArray(response.characters)) throw new Error("user_characters_response_invalid");
  return response.characters.map((candidate) => {
    const character = objectValue(candidate, "user_characters_response_invalid");
    if (typeof character.id !== "string" || !CHARACTER_ID_PATTERN.test(character.id)
      || !new Set(["system", "player"]).has(character.kind as string)
      || typeof character.is_mine !== "boolean" || typeof character.is_avatar !== "boolean" || typeof character.can_manage !== "boolean") {
      throw new Error("user_characters_response_invalid");
    }
    return {
      id: character.id,
      name: boundedText(character.name, 100, "user_characters_response_invalid"),
      kind: character.kind as UserCharacterSummary["kind"],
      is_mine: character.is_mine,
      is_avatar: character.is_avatar,
      can_manage: character.can_manage,
      binding_version: bindingVersion(character.binding_version, "user_characters_response_invalid"),
      creator_uid: character.creator_uid === null ? null : boundedText(character.creator_uid, 20, "user_characters_response_invalid"),
      creator_name: character.creator_name === null ? null : boundedText(character.creator_name, 100, "user_characters_response_invalid"),
      creator_revision: bindingVersion(character.creator_revision, "user_characters_response_invalid"),
      usage_count: bindingVersion(character.usage_count, "user_characters_response_invalid"),
    };
  });
}

function parseVoiceSource(value: unknown): VoiceSource {
  const source = objectValue(value, "voice_sources_response_invalid");
  if (typeof source._id !== "string" || !/^[0-9a-f]{32}$/.test(source._id)
    || !Array.isArray(source.languages) || source.languages.some((language) => typeof language !== "string" || language.length > 100)
    || !Array.isArray(source.tags) || source.tags.some((tag) => typeof tag !== "string" || tag.length === 0 || tag.length > 100)
    || !Array.isArray(source.samples)) throw new Error("voice_sources_response_invalid");
  const firstSample = source.samples[0] === undefined ? null : objectValue(source.samples[0], "voice_sources_response_invalid");
  const sampleUrl = firstSample?.audio ?? null;
  if (!(sampleUrl === null || (typeof sampleUrl === "string" && sampleUrl.startsWith("/v1/discord-settings/voices/") && !sampleUrl.startsWith("//") && sampleUrl.length <= 512))) {
    throw new Error("voice_sources_response_invalid");
  }
  return {
    id: source._id,
    title: boundedText(source.title, 200, "voice_sources_response_invalid"),
    language: (source.languages as string[]).join(" · "),
    tags: [...new Set(source.tags as string[])],
    sample_url: sampleUrl as string | null,
  };
}

export function parseVoiceSourcePage(value: unknown, pageNumber: number, pageSize: number): VoiceSourcePage {
  const page = objectValue(value, "voice_sources_response_invalid");
  if (!Array.isArray(page.items)
    || typeof page.total !== "number" || !Number.isSafeInteger(page.total) || page.total < 0
    || !(page.has_more === null || typeof page.has_more === "boolean")
    || !(page.accessible_upper_bound === null || (typeof page.accessible_upper_bound === "number" && Number.isSafeInteger(page.accessible_upper_bound) && page.accessible_upper_bound >= 0))
    || typeof page.window_limited !== "boolean"
    || typeof page.total_is_exact !== "boolean") throw new Error("voice_sources_response_invalid");
  const upperBound = (page.accessible_upper_bound as number | null) ?? page.total;
  const hasNext = page.has_more === true || (page.has_more === null && pageNumber * pageSize < upperBound);
  return {
    items: page.items.map(parseVoiceSource),
    next_cursor: hasNext ? String(pageNumber + 1) : null,
    window_limited: page.window_limited,
    total_is_exact: page.total_is_exact,
    accessible_upper_bound: upperBound,
  };
}

export function parseUserCharacterVoice(value: unknown): UserCharacterVoice {
  const voice = objectValue(value, "user_character_voice_response_invalid");
  if (typeof voice.id !== "string" || !CHARACTER_ID_PATTERN.test(voice.id)
    || !(voice.voice_id === null || (typeof voice.voice_id === "string" && /^[0-9a-f]{32}$/.test(voice.voice_id)))
    || !(voice.admin_voice_id === null || (typeof voice.admin_voice_id === "string" && /^[0-9a-f]{32}$/.test(voice.admin_voice_id)))
    || typeof voice.effective_voice_id !== "string" || !/^[0-9a-f]{32}$/.test(voice.effective_voice_id)
    || !new Set(["user", "admin", "default"]).has(voice.effective_source as string)) {
    throw new Error("user_character_voice_response_invalid");
  }
  const ownSettings = voice.synthesis_settings === null ? null : synthesisSettings(voice.synthesis_settings, "user_character_voice_response_invalid");
  const effectiveSettings = synthesisSettings(voice.effective_synthesis_settings, "user_character_voice_response_invalid");
  return {
    id: voice.id,
    name: boundedText(voice.name, 100, "user_character_voice_response_invalid"),
    voice_id: voice.voice_id as string | null,
    admin_voice_id: voice.admin_voice_id as string | null,
    effective_voice_id: voice.effective_voice_id,
    effective_source: voice.effective_source as UserCharacterVoice["effective_source"],
    synthesis_settings: ownSettings,
    effective_synthesis_settings: effectiveSettings,
    voice_revision: bindingVersion(voice.voice_revision, "user_character_voice_response_invalid"),
  };
}

export function parseAdminCharacterDeleteResult(value: unknown): AdminCharacterDeleteResult {
  const result = objectValue(value, "admin_character_delete_response_invalid");
  if (result.ok !== true || typeof result.id !== "string" || !CHARACTER_ID_PATTERN.test(result.id)) {
    throw new Error("admin_character_delete_response_invalid");
  }
  return { ok: true, id: result.id };
}

export function parseAdminCharacterProfile(value: unknown): AdminCharacterProfile {
  const profile = objectValue(value, "admin_character_profile_response_invalid");
  if (typeof profile.id !== "string" || !CHARACTER_ID_PATTERN.test(profile.id)) throw new Error("admin_character_profile_response_invalid");
  return {
    id: profile.id,
    name: boundedText(profile.name, 100, "admin_character_profile_response_invalid"),
    content: boundedText(profile.content, 20_000, "admin_character_profile_response_invalid"),
    profile_revision: bindingVersion(profile.profile_revision, "admin_character_profile_response_invalid"),
  };
}

export function parseAdminCharacterSdPrompts(value: unknown): AdminCharacterSdPrompts {
  const prompts = objectValue(value, "admin_character_sd_prompts_response_invalid");
  if (typeof prompts.id !== "string" || !CHARACTER_ID_PATTERN.test(prompts.id)
    || typeof prompts.positive_prompt !== "string" || prompts.positive_prompt.trim().length === 0 || prompts.positive_prompt.length > 4_000
    || typeof prompts.negative_prompt !== "string" || prompts.negative_prompt.length > 4_000) {
    throw new Error("admin_character_sd_prompts_response_invalid");
  }
  return {
    id: prompts.id,
    name: boundedText(prompts.name, 100, "admin_character_sd_prompts_response_invalid"),
    positive_prompt: prompts.positive_prompt,
    negative_prompt: prompts.negative_prompt,
    sd_revision: bindingVersion(prompts.sd_revision, "admin_character_sd_prompts_response_invalid"),
  };
}

export function parseUserCharacterImagePrompts(value: unknown): UserCharacterImagePrompts {
  const prompts = objectValue(value, "user_character_image_prompts_response_invalid");
  const nullablePrompt = (candidate: unknown, required: boolean): string | null => {
    if (candidate === null && !required) return null;
    if (typeof candidate !== "string" || candidate.length > 4_000 || (required && candidate.trim().length === 0)) throw new Error("user_character_image_prompts_response_invalid");
    return candidate;
  };
  const completePrompt = (candidate: unknown, required: boolean): string => {
    if (typeof candidate !== "string" || candidate.length > 4_000 || (required && candidate.trim().length === 0)) throw new Error("user_character_image_prompts_response_invalid");
    return candidate;
  };
  if (typeof prompts.id !== "string" || !CHARACTER_ID_PATTERN.test(prompts.id)
    || (prompts.effective_source !== "user" && prompts.effective_source !== "shared")) throw new Error("user_character_image_prompts_response_invalid");
  const positivePrompt = nullablePrompt(prompts.positive_prompt, false);
  const negativePrompt = nullablePrompt(prompts.negative_prompt, false);
  const sharedPositive = completePrompt(prompts.shared_positive_prompt, true);
  const sharedNegative = completePrompt(prompts.shared_negative_prompt, false);
  const effectivePositive = completePrompt(prompts.effective_positive_prompt, true);
  const effectiveNegative = completePrompt(prompts.effective_negative_prompt, false);
  if ((positivePrompt === null) !== (negativePrompt === null)
    || (prompts.effective_source === "user") !== (positivePrompt !== null)
    || (prompts.effective_source === "user" && (effectivePositive !== positivePrompt || effectiveNegative !== negativePrompt))
    || (prompts.effective_source === "shared" && (effectivePositive !== sharedPositive || effectiveNegative !== sharedNegative))) throw new Error("user_character_image_prompts_response_invalid");
  return {
    id: prompts.id,
    name: boundedText(prompts.name, 100, "user_character_image_prompts_response_invalid"),
    positive_prompt: positivePrompt,
    negative_prompt: negativePrompt,
    shared_positive_prompt: sharedPositive,
    shared_negative_prompt: sharedNegative,
    effective_positive_prompt: effectivePositive,
    effective_negative_prompt: effectiveNegative,
    effective_source: prompts.effective_source,
    image_prompt_revision: bindingVersion(prompts.image_prompt_revision, "user_character_image_prompts_response_invalid"),
  };
}

export function parseAdminCharacterVoice(value: unknown): AdminCharacterVoice {
  const voice = objectValue(value, "admin_character_voice_response_invalid");
  const voiceId = voice.voice_id;
  const effectiveVoiceId = voice.effective_voice_id;
  if (
    typeof voice.id !== "string"
    || !CHARACTER_ID_PATTERN.test(voice.id)
    || !(voiceId === null || (typeof voiceId === "string" && /^[0-9a-f]{32}$/.test(voiceId)))
    || typeof effectiveVoiceId !== "string"
    || !/^[0-9a-f]{32}$/.test(effectiveVoiceId)
    || (typeof voiceId === "string" && effectiveVoiceId !== voiceId)
  ) throw new Error("admin_character_voice_response_invalid");
  const ownSettings = voice.synthesis_settings === null ? null : synthesisSettings(voice.synthesis_settings, "admin_character_voice_response_invalid");
  const effectiveSettings = synthesisSettings(voice.effective_synthesis_settings, "admin_character_voice_response_invalid");
  return {
    id: voice.id,
    name: boundedText(voice.name, 100, "admin_character_voice_response_invalid"),
    voice_id: voiceId as string | null,
    effective_voice_id: effectiveVoiceId,
    synthesis_settings: ownSettings,
    effective_synthesis_settings: effectiveSettings,
    voice_revision: bindingVersion(voice.voice_revision, "admin_character_voice_response_invalid"),
  };
}

export function parseManualImageJob(value: unknown): ManualImagePublicJob {
  const code = "manual_image_job_response_invalid";
  const job = objectValue(value, code);
  const statuses = new Set(["queued", "claimed", "generated", "delivered", "failed", "outcome_unknown"]);
  const metadata = objectValue(job.metadata, code);
  const provenance = objectValue(job.metadata_provenance, code);
  const generalSources = new Set<ManualImageMetadataSource>(["request", "settings_snapshot", "generation_result", "unavailable"]);
  const httpsUrl = (candidate: unknown): candidate is string => {
    if (typeof candidate !== "string" || candidate.length === 0 || candidate.length > 2_048) return false;
    try { const url = new URL(candidate); return url.protocol === "https:" && !url.username && !url.password; }
    catch { return false; }
  };
  const requiredString = (candidate: unknown, maximum: number, allowEmpty = false): candidate is string =>
    typeof candidate === "string" && candidate.length <= maximum && (allowEmpty || candidate.trim().length > 0) && !/[\u0000-\u0008\u000b\u000c\u000e-\u001f\u007f]/u.test(candidate);
  const derivedSource = discordImageJobSourceFromRequestId(typeof job.request_id === "string" ? job.request_id : "");
  const source = job.source === undefined ? derivedSource : job.source;
  if (
    typeof job.id !== "string" || !/^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(job.id)
    || !requiredString(job.request_id, 128)
    || (source !== "dialogue" && source !== "command" && source !== "manual")
    || source !== derivedSource
    || !statuses.has(job.status as string)
    || !(job.recovery_pending === undefined || typeof job.recovery_pending === "boolean")
    || !requiredString(metadata.positive_prompt, 8_000)
    || !requiredString(metadata.negative_prompt, 4_000, true)
    || !requiredString(metadata.checkpoint_version, 1_000)
    || typeof metadata.steps !== "number" || !Number.isSafeInteger(metadata.steps) || metadata.steps < 1 || metadata.steps > 100
    || !requiredString(metadata.sampler, 64)
    || !requiredString(metadata.scheduler, 64)
    || !requiredString(metadata.cfg, 32)
    || !requiredString(metadata.resolution, 32)
    || !(metadata.seed === null || (typeof metadata.seed === "number" && Number.isSafeInteger(metadata.seed) && metadata.seed >= 0))
    || provenance.positive_prompt !== "request"
    || provenance.negative_prompt !== "request"
    || !["checkpoint_version", "steps", "sampler", "scheduler", "cfg", "resolution"].every((key) => generalSources.has(provenance[key] as ManualImageMetadataSource))
    || (provenance.seed !== "generation_result" && provenance.seed !== "unavailable")
    || (metadata.seed === null) !== (provenance.seed === "unavailable")
    || !(job.generated_image === null || (typeof job.generated_image === "object" && job.generated_image !== null && httpsUrl((job.generated_image as Record<string, unknown>).url)))
    || !(job.error === null || (typeof job.error === "object" && job.error !== null && typeof (job.error as Record<string, unknown>).code === "string" && /^[a-z0-9][a-z0-9_.:-]{0,127}$/i.test((job.error as { code: string }).code)))
    || !timestamp(job.created_at, code)
    || !timestamp(job.updated_at, code)
  ) throw new Error(code);
  if (job.delivery !== null) {
    const delivery = objectValue(job.delivery, code);
    if (
      delivery.guild_id !== "1515554951219118133"
      || delivery.channel_id !== "1545195379409289259"
      || typeof delivery.message_id !== "string"
      || !/^\d{17,20}$/.test(delivery.message_id)
      || !httpsUrl(delivery.url)
    ) throw new Error(code);
  }
  return { ...job, source, recovery_pending: job.recovery_pending === true } as unknown as ManualImagePublicJob;
}

const IMAGE_PRESET_ID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

function parseImagePromptPresetItem(value: unknown): ImagePromptPreset {
  const code = "image_preset_response_invalid";
  const preset = objectValue(value, code);
  const title = (candidate: unknown): candidate is string =>
    typeof candidate === "string" && candidate.length <= 100 && candidate.trim().length > 0
      && !/[\u0000-\u001f\u007f]/u.test(candidate);
  // textarea 的换行、制表符是合法题词，不应在客户端协议层意外丢弃。
  const prompt = (candidate: unknown, maximum: number, allowEmpty = false): candidate is string =>
    typeof candidate === "string" && candidate.length <= maximum && (allowEmpty || candidate.trim().length > 0)
      && !/[\u0000-\u0008\u000b\u000c\u000e-\u001f\u007f]/u.test(candidate);
  if (
    typeof preset.id !== "string" || !IMAGE_PRESET_ID_PATTERN.test(preset.id)
    || !title(preset.title)
    || !prompt(preset.positive_prompt, 8_000)
    || !prompt(preset.negative_prompt, 4_000, true)
    || typeof preset.revision !== "number" || !Number.isSafeInteger(preset.revision) || preset.revision < 0
    || !timestamp(preset.created_at, code)
    || !timestamp(preset.updated_at, code)
  ) throw new Error(code);
  return preset as unknown as ImagePromptPreset;
}

export function parseImagePromptPresetPage(value: unknown): ImagePromptPresetPage {
  const code = "image_preset_page_response_invalid";
  const page = objectValue(value, code);
  if (!Array.isArray(page.presets)) throw new Error(code);
  if (page.next_cursor !== null && (typeof page.next_cursor !== "string" || page.next_cursor.length === 0 || page.next_cursor.length > 512)) {
    throw new Error(code);
  }
  try {
    return { presets: page.presets.map(parseImagePromptPresetItem), next_cursor: page.next_cursor as string | null };
  } catch {
    throw new Error(code);
  }
}

export function parseImagePromptPreset(value: unknown): ImagePromptPreset {
  return parseImagePromptPresetItem(value);
}

export function parseImageUsageSnapshot(value: unknown): ImageUsageSnapshot {
  const code = "image_usage_response_invalid";
  const snapshot = objectValue(value, code);
  const personal = objectValue(snapshot.personal, code);
  const platform = objectValue(snapshot.platform, code);
  const used = (candidate: unknown) => {
    if (typeof candidate !== "number" || !Number.isSafeInteger(candidate) || candidate < 0) throw new Error(code);
    return candidate;
  };
  const personalLimit = personal.limit === null ? null : used(personal.limit);
  // 用量端点沿用 quota 合同的 Unix 秒；兼容此前客户端预期的 ISO 时间。
  const resetMs = typeof snapshot.resets_at === "number" && Number.isSafeInteger(snapshot.resets_at) && snapshot.resets_at > 0
    ? snapshot.resets_at * 1_000
    : typeof snapshot.resets_at === "string" ? Date.parse(snapshot.resets_at) : Number.NaN;
  if (
    typeof snapshot.limit_exempt !== "boolean"
    || !Number.isFinite(resetMs) || Number.isNaN(new Date(resetMs).getTime())
  ) throw new Error(code);
  return {
    personal: { used: used(personal.used), limit: personalLimit },
    platform: { used: used(platform.used), limit: used(platform.limit) },
    resets_at: new Date(resetMs).toISOString(),
    limit_exempt: snapshot.limit_exempt,
  };
}

export function parseManualImageHistoryClear(value: unknown): { cleared: number } {
  const response = objectValue(value, "manual_image_history_clear_response_invalid");
  if (typeof response.cleared !== "number" || !Number.isSafeInteger(response.cleared) || response.cleared < 0) {
    throw new Error("manual_image_history_clear_response_invalid");
  }
  return { cleared: response.cleared };
}

export function parseAdminCharacterImage(value: unknown): AdminCharacterImage {
  const image = objectValue(value, "admin_character_image_response_invalid");
  if (typeof image.id !== "string" || !CHARACTER_ID_PATTERN.test(image.id)
    || (image.kind !== "portrait" && image.kind !== "cover")
    || !isCharacterImageUrl(image.url, image.id, image.kind)
    || !(image.width === undefined || (typeof image.width === "number" && Number.isSafeInteger(image.width) && image.width > 0))
    || !(image.height === undefined || (typeof image.height === "number" && Number.isSafeInteger(image.height) && image.height > 0))) {
    throw new Error("admin_character_image_response_invalid");
  }
  const result: AdminCharacterImage = {
    id: image.id,
    kind: image.kind,
    url: image.url,
    revision: bindingVersion(image.revision, "admin_character_image_response_invalid"),
  };
  if (typeof image.width === "number") result.width = image.width;
  if (typeof image.height === "number") result.height = image.height;
  return result;
}

export function parseGuildMemberPage(value: unknown): GuildMemberPage {
  const record = objectValue(value, "guild_members_response_invalid");
  if (!Array.isArray(record.members)) throw new Error("guild_members_response_invalid");
  const members = record.members.map((item) => {
    const member = objectValue(item, "guild_members_response_invalid");
    if (typeof member.uid !== "string" || !DISCORD_UID_PATTERN.test(member.uid)
      || !(member.avatar_url === null || (typeof member.avatar_url === "string" && member.avatar_url.length > 0 && member.avatar_url.length <= 2_048 && !/[\u0000-\u001f\u007f]/u.test(member.avatar_url)))) {
      throw new Error("guild_members_response_invalid");
    }
    return { uid: member.uid, name: boundedText(member.name, 100, "guild_members_response_invalid"), avatar_url: member.avatar_url as string | null };
  });
  return { members, next_cursor: nullableCursor(record.next_cursor, "guild_members_response_invalid") };
}

function parseInteractionSummary(value: unknown): { id: string; character_id?: string; character_name: string; started_at: string; expires_at: string; message_count: number } {
  const block = objectValue(value, "interaction_blocks_response_invalid");
  if (typeof block.id !== "string" || !OPAQUE_ID_PATTERN.test(block.id)
    || typeof block.message_count !== "number" || !Number.isInteger(block.message_count) || block.message_count < 0) {
    throw new Error("interaction_blocks_response_invalid");
  }
  if (block.character_id !== undefined && (typeof block.character_id !== "string" || !CHARACTER_ID_PATTERN.test(block.character_id))) throw new Error("interaction_blocks_response_invalid");
  return {
    id: block.id,
    ...(typeof block.character_id === "string" ? { character_id: block.character_id } : {}),
    character_name: boundedText(block.character_name, 100, "interaction_blocks_response_invalid"),
    started_at: timestamp(block.started_at, "interaction_blocks_response_invalid"),
    expires_at: timestamp(block.expires_at, "interaction_blocks_response_invalid"),
    message_count: block.message_count,
  };
}

export function parseInteractionBlockPage(value: unknown): InteractionBlockPage {
  const record = objectValue(value, "interaction_blocks_response_invalid");
  if (!Array.isArray(record.blocks)) throw new Error("interaction_blocks_response_invalid");
  let filters;
  if (record.filter_characters !== undefined) {
    if (!Array.isArray(record.filter_characters)) throw new Error("interaction_blocks_response_invalid");
    filters = record.filter_characters.map(value => {
      const item = objectValue(value, "interaction_blocks_response_invalid");
      if (typeof item.id !== "string" || !CHARACTER_ID_PATTERN.test(item.id)) throw new Error("interaction_blocks_response_invalid");
      return { id: item.id, name: boundedText(item.name, 100, "interaction_blocks_response_invalid") };
    });
  }
  return { blocks: record.blocks.map(parseInteractionSummary), next_cursor: nullableCursor(record.next_cursor, "interaction_blocks_response_invalid"), ...(filters ? { filter_characters: filters } : {}) };
}

export function parseInteractionBlockDetail(value: unknown): InteractionBlockDetail {
  const block = objectValue(value, "interaction_detail_response_invalid");
  if (typeof block.id !== "string" || !OPAQUE_ID_PATTERN.test(block.id) || !Array.isArray(block.messages)) throw new Error("interaction_detail_response_invalid");
  const characterName = boundedText(block.character_name, 100, "interaction_detail_response_invalid");
  const messages = block.messages.map((item) => {
    const message = objectValue(item, "interaction_detail_response_invalid");
    if (message.role !== "user" && message.role !== "assistant") throw new Error("interaction_detail_response_invalid");
    const role: "user" | "assistant" = message.role;
    const speaker = boundedText(message.speaker, 100, "interaction_detail_response_invalid");
    if ((role === "user" && !/^用户[A-Z]+$/.test(speaker)) || (role === "assistant" && speaker !== characterName)) {
      throw new Error("interaction_detail_response_invalid");
    }
    if (typeof message.content !== "string" || message.content.length > 1_000_000) throw new Error("interaction_detail_response_invalid");
    return { speaker, role, content: message.content, created_at: timestamp(message.created_at, "interaction_detail_response_invalid") };
  });
  return {
    id: block.id,
    character_name: characterName,
    started_at: timestamp(block.started_at, "interaction_detail_response_invalid"),
    expires_at: timestamp(block.expires_at, "interaction_detail_response_invalid"),
    messages,
  };
}

export function parseAdminUsageSnapshot(value: unknown): AdminUsageSnapshot {
  const record = objectValue(value, "admin_usage_response_invalid");
  const costs = objectValue(record.costs, "admin_usage_response_invalid");
  const daily = objectValue(record.image_daily, "admin_usage_response_invalid");
  const ttsDaily = objectValue(record.tts_daily, "admin_usage_response_invalid");
  const windows = objectValue(costs.windows, "admin_usage_response_invalid");
  const parseWindow = (value: unknown) => { const item = objectValue(value, "admin_usage_response_invalid"); if (typeof item.starts_at !== "string" || typeof item.ends_at !== "string") throw new Error("admin_usage_response_invalid"); return { starts_at:item.starts_at, ends_at:item.ends_at }; };
  const nullableInteger = (value: unknown) => value === null ? null : (() => { const number = nonNegativeNumber(value,"admin_usage_response_invalid"); if (!Number.isSafeInteger(number)) throw new Error("admin_usage_response_invalid"); return number; })();
  const imageUsers = Array.isArray(daily.users) ? daily.users.map((value) => { const item=objectValue(value,"admin_usage_response_invalid"); if(typeof item.user_id!=="string") throw new Error("admin_usage_response_invalid"); return {user_id:item.user_id,used:nonNegativeNumber(item.used,"admin_usage_response_invalid")}; }) : [];
  const ttsUsers = Array.isArray(ttsDaily.users) ? ttsDaily.users.map((value) => { const item=objectValue(value,"admin_usage_response_invalid"); if(typeof item.user_id!=="string") throw new Error("admin_usage_response_invalid"); return {user_id:item.user_id,used_ms:nonNegativeNumber(item.used_ms,"admin_usage_response_invalid")}; }) : [];
  const pendingDuration: AdminUsageSnapshot["tts_daily"]["pending_duration"] = Array.isArray(ttsDaily.pending_duration) ? ttsDaily.pending_duration.map((value) => { const item=objectValue(value,"admin_usage_response_invalid"); if(typeof item.request_id!=="string"||typeof item.user_id!=="string"||(item.billing_status!=="confirmed"&&item.billing_status!=="uncertain")) throw new Error("admin_usage_response_invalid"); return {request_id:item.request_id,user_id:item.user_id,billing_status:item.billing_status as "confirmed"|"uncertain",reserved_at_ms:nonNegativeNumber(item.reserved_at_ms,"admin_usage_response_invalid")}; }) : [];
  if (
    record.currency !== "USD"
    || costs.currency !== "USD"
    || costs.time_zone !== "Asia/Shanghai"
    || typeof daily.reset_at !== "string"
    || daily.time_zone !== "UTC"
    || typeof ttsDaily.reset_at !== "string"
    || ttsDaily.time_zone !== "Asia/Shanghai"
    || !["s2.1-pro","s2.1-pro-free","s2-pro","s1"].includes(String(ttsDaily.engine))
  ) throw new Error("admin_usage_response_invalid");
  const limit = nonNegativeNumber(daily.limit, "admin_usage_response_invalid");
  const maxLimit = nonNegativeNumber(daily.max_limit, "admin_usage_response_invalid");
  if (!Number.isInteger(limit) || !Number.isInteger(maxLimit) || limit > maxLimit) throw new Error("admin_usage_response_invalid");
  return {
    currency: "USD",
    llm: {h24:nonNegativeNumber(objectValue(record.llm,"admin_usage_response_invalid").h24,"admin_usage_response_invalid"),d7:nonNegativeNumber(objectValue(record.llm,"admin_usage_response_invalid").d7,"admin_usage_response_invalid"),d30:nonNegativeNumber(objectValue(record.llm,"admin_usage_response_invalid").d30,"admin_usage_response_invalid")},
    image: {h24:nonNegativeNumber(objectValue(record.image,"admin_usage_response_invalid").h24,"admin_usage_response_invalid"),d7:nonNegativeNumber(objectValue(record.image,"admin_usage_response_invalid").d7,"admin_usage_response_invalid"),d30:nonNegativeNumber(objectValue(record.image,"admin_usage_response_invalid").d30,"admin_usage_response_invalid")},
    costs: {currency:"USD",time_zone:"Asia/Shanghai",windows:{ today:parseWindow(windows.today), d7:parseWindow(windows.d7), d30:parseWindow(windows.d30) },llm:usageWindow(costs.llm),image:usageWindow(costs.image),tts:usageWindow(costs.tts)},
    image_daily: {
      limit,
      user_limit: nullableInteger(daily.user_limit),
      max_limit: maxLimit,
      used: nonNegativeNumber(daily.used, "admin_usage_response_invalid"),
      remaining: nonNegativeNumber(daily.remaining, "admin_usage_response_invalid"),
      reset_at: daily.reset_at,
      time_zone: "UTC",
      users: imageUsers,
    },
    tts_daily: {
      engine: ttsDaily.engine as AdminUsageSnapshot["tts_daily"]["engine"],
      global_limit_ms: nullableInteger(ttsDaily.global_limit_ms),
      user_limit_ms: nullableInteger(ttsDaily.user_limit_ms),
      used_ms: nonNegativeNumber(ttsDaily.used_ms,"admin_usage_response_invalid"),
      reset_at: ttsDaily.reset_at,
      time_zone: "Asia/Shanghai",
      revision: nullableInteger(ttsDaily.revision) ?? 0,
      users: ttsUsers,
      pending_duration: pendingDuration,
    },
  };
}

export function parseAdminContextPolicySnapshot(value: unknown): AdminContextPolicySnapshot {
  const record = objectValue(value, "admin_context_policy_response_invalid");
  if (
    !isDiscordContextMaxMessages(record.context_max_messages)
    || record.half_window_messages !== Math.floor(record.context_max_messages / 2)
    || typeof record.revision !== "number"
    || !Number.isSafeInteger(record.revision)
    || record.revision < 0
  ) throw new Error("admin_context_policy_response_invalid");
  return {
    context_max_messages: record.context_max_messages,
    half_window_messages: record.half_window_messages,
    revision: record.revision,
  };
}

export function parseAdminRoles(value: unknown): AdminRole[] {
  const record = objectValue(value, "admin_roles_response_invalid");
  if (!Array.isArray(record.roles)) throw new Error("admin_roles_response_invalid");
  return record.roles.map((item) => {
    const role = objectValue(item, "admin_roles_response_invalid");
    if (
      typeof role.id !== "string"
      || !/^\d{17,20}$/.test(role.id)
      || typeof role.name !== "string"
      || role.name.trim().length === 0
      || typeof role.color !== "number"
      || !Number.isInteger(role.color)
      || role.color < 0
      || role.color > 0xffffff
      || !(role.icon_url === null || typeof role.icon_url === "string")
      || !(role.unicode_emoji === null || typeof role.unicode_emoji === "string")
      || typeof role.custom !== "boolean"
      || typeof role.system !== "boolean"
      || typeof role.locked !== "boolean"
      || role.custom === role.system
    ) throw new Error("admin_roles_response_invalid");
    return role as unknown as AdminRole;
  });
}
